import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Code, Plus, X, Edit3, Trash2, Tag } from 'lucide-react';
import api from '../components/api';
import { toast } from 'react-toastify';
import useGlobale from '../components/global';
import Sidebar from '../components/SideBar';
import Header from '../components/Header';
import DemandeurMenu from '../components/DemandeurMenu';
import Select from 'react-select';

const CompetencesPage = () => {
    const { user } = useGlobale();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [availableCompetences, setAvailableCompetences] = useState([]);
    const [selectedCompetences, setSelectedCompetences] = useState([]);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    // Set sidebar initial state based on screen size
    useEffect(() => {
        const handleResize = () => {
            setIsSidebarOpen(window.innerWidth >= 1024);
        };
        
        // Set initial state
        handleResize();
        
        // Add event listener
        window.addEventListener('resize', handleResize);
        
        // Cleanup
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchAvailableCompetences(), fetchUserCompetences()]);
            setLoading(false);
        };
        loadData();
    }, []);

    const fetchAvailableCompetences = async () => {
        try {
            const response = await api.get('demandeur/competences/');
            setAvailableCompetences(response.data);
        } catch (error) {
            console.error('Erreur lors de la récupération des compétences:', error);
            toast.error('Erreur lors de la récupération des compétences disponibles');
        }
    };

    const fetchUserCompetences = async () => {
        try {
            const response = await api.get('demandeur/competences-candidat/');
            setSelectedCompetences(response.data.map(c => c.competence));
        } catch (error) {
            console.error('Erreur lors de la récupération des compétences utilisateur:', error);
            toast.error('Erreur lors de la récupération de vos compétences');
        }
    };

    const handleCompetenceChange = async (selectedOptions) => {
        const selectedIds = selectedOptions.map(option => option.value);
        
        try {
            // Supprimer d'abord toutes les compétences existantes
            await api.delete('demandeur/competences-candidat/');
            
            // Ajouter les nouvelles compétences sélectionnées
            if (selectedIds.length > 0) {
                await api.post('demandeur/competences-candidat/', {
                    competence: selectedIds
                });
            }
            
            setSelectedCompetences(selectedIds);
            toast.success('Compétences mises à jour avec succès');
            fetchUserCompetences();
        } catch (error) {
            console.error('Erreur lors de la mise à jour des compétences:', error);
            toast.error('Erreur lors de la mise à jour des compétences');
        }
    };

    // Ajouter les styles personnalisés pour react-select
    const customSelectStyles = {
        control: (base, state) => ({
            ...base,
            borderColor: state.isFocused ? '#f97316' : '#e5e7eb',
            boxShadow: state.isFocused ? '0 0 0 1px #f97316' : base.boxShadow,
            '&:hover': {
                borderColor: '#f97316'
            },
            borderRadius: '0.5rem',
            padding: '2px'
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#f97316' : state.isFocused ? '#fed7aa' : base.backgroundColor,
            '&:active': {
                backgroundColor: '#f97316'
            }
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: '#fff7ed',
            borderRadius: '0.375rem'
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: '#c2410c'
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: '#c2410c',
            '&:hover': {
                backgroundColor: '#fed7aa',
                color: '#9a3412'
            }
        })
    };

    // Modifier le rendu des compétences
    const renderSelectedCompetences = () => (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {availableCompetences
                .filter(comp => selectedCompetences.includes(comp.id))
                .map(comp => (
                    <div 
                        key={comp.id}
                        className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
                    >
                        <div className="p-4 flex items-start space-x-3">
                            <div className="flex-shrink-0">
                                <div className="p-2 bg-orange-100 rounded-lg">
                                    <Tag className="h-5 w-5 text-orange-600" />
                                </div>
                            </div>
                            <div className="flex-1">
                                <h3 className="text-gray-800 font-medium leading-5">
                                    {comp.nom}
                                </h3>
                            </div>
                        </div>
                    </div>
                ))
            }
        </div>
    );

    // Modifier la partie du modal de confirmation
    const ConfirmationModal = () => (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
                <div className="p-6">
                    <div className="flex items-center justify-center mb-4">
                        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                            <Trash2 className="h-6 w-6 text-red-600" />
                        </div>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                        Confirmer la suppression
                    </h3>
                    <p className="text-gray-600 text-center mb-6">
                        Êtes-vous sûr de vouloir supprimer toutes vos compétences ? Cette action est irréversible.
                    </p>
                    <div className="flex justify-center space-x-3">
                        <button
                            onClick={() => setShowConfirmModal(false)}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                        >
                            Annuler
                        </button>
                        <button
                            onClick={() => {
                                handleDeleteAll();
                                setShowConfirmModal(false);
                            }}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
                        >
                            Supprimer tout
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderContent = () => {
        if (loading) {
            return (
                <div className="flex justify-center items-center h-full">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-orange-500"></div>
                </div>
            );
        }

        return (
            <>
                <div className="mb-8">
                    <h1 className="text-2xl font-bold text-[#437011] mb-2">
                        Mes Compétences
                    </h1>
                    <p className="text-gray-600">Sélectionnez les compétences que vous maîtrisez</p>
                </div>

                {selectedCompetences.length > 0 ? (
                    <div className="bg-white rounded-xl shadow-md p-6">
                        <div className="flex justify-between items-center mb-6">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800">
                                    Vos compétences
                                </h3>
                                <p className="text-sm text-gray-600 mt-1">
                                    {selectedCompetences.length} compétence{selectedCompetences.length > 1 ? 's' : ''} sélectionnée{selectedCompetences.length > 1 ? 's' : ''}
                                </p>
                            </div>
                            <div className="flex space-x-3">
                                <button
                                    onClick={() => setShowModal(true)}
                                    className="inline-flex items-center px-3 py-2 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 transition-colors duration-200"
                                >
                                    <Edit3 size={16} className="mr-2" />
                                    Modifier
                                </button>
                            </div>
                        </div>
                        {renderSelectedCompetences()}
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-md p-8 text-center">
                        <div className="mb-6">
                            <div className="h-20 w-20 rounded-full bg-orange-100 flex items-center justify-center text-orange-500 mx-auto">
                                <Code size={36} strokeWidth={1.5} />
                            </div>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-800 mb-3">Aucune compétence sélectionnée</h3>
                        <p className="text-gray-600 mb-6 max-w-md mx-auto">
                            Ajoutez vos compétences pour mettre en valeur votre profil auprès des recruteurs.
                        </p>
                        <button
                            onClick={() => setShowModal(true)}
                            className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-200"
                        >
                            <Plus size={20} className="mr-2" />
                            Ajouter des compétences
                        </button>
                    </div>
                )}

                {/* Modal pour sélectionner les compétences */}
                {showModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                                <h3 className="text-lg font-semibold text-gray-800">
                                    Gérer vos compétences
                                </h3>
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    <X size={24} />
                                </button>
                            </div>
                            <div className="p-6">
                                <Select
                                    isMulti
                                    options={availableCompetences.map(comp => ({
                                        value: comp.id,
                                        label: comp.nom
                                    }))}
                                    value={availableCompetences
                                        .filter(comp => selectedCompetences.includes(comp.id))
                                        .map(comp => ({
                                            value: comp.id,
                                            label: comp.nom
                                        }))}
                                    onChange={handleCompetenceChange}
                                    styles={customSelectStyles}
                                    placeholder="Sélectionnez vos compétences..."
                                    noOptionsMessage={() => "Aucune compétence disponible"}
                                />
                                <div className="mt-6 flex justify-end space-x-3">
                                    <button
                                        onClick={() => setShowModal(false)}
                                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                                    >
                                        Fermer
                                    </button>
                                    {selectedCompetences.length > 0 && (
                                        <button
                                            onClick={() => setShowConfirmModal(true)}
                                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
                                        >
                                            Tout supprimer
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de confirmation de suppression */}
                {showConfirmModal && <ConfirmationModal />}
            </>
        );
    };

    const handleDeleteAll = async () => {
        try {
            await api.delete('demandeur/competences-candidat/');
            setSelectedCompetences([]);
            setShowModal(false);
            toast.success('Toutes les compétences ont été supprimées');
        } catch (error) {
            console.error('Erreur lors de la suppression des compétences:', error);
            toast.error('Erreur lors de la suppression des compétences');
        }
    };

    return (
        <div className="flex min-h-screen bg-[#F4F8E9]">
            <Sidebar
                isOpen={isSidebarOpen}
                menuItems={DemandeurMenu()}
                onClose={() => setIsSidebarOpen(false)}
            />

            <div className="flex-1 flex flex-col w-full">
                <Header
                    isSidebarOpen={isSidebarOpen}
                    setIsSidebarOpen={setIsSidebarOpen}
                    user={user}
                />
                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 pb-20 lg:pb-8 w-full">
                    {renderContent()}
                </main>
            </div>
        </div>
    );
};

export default CompetencesPage;
